import { Due } from "@t/Resort"
import { useEffect, useMemo, useRef, useState } from "react"
import { BarController, BarElement, Chart } from "chart.js"
import styles from "@css/modules/Listing.module.scss"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { LongArrowDown } from "@css/icons/LongArrowDown"

Chart.register([BarElement, BarController, BarElement, ChartDataLabels])
Chart.defaults.set("plugins.datalabels", {
    color: "transparent"
})

const elementsToShowDesktop = 12
const elementsToShowMobile = 8

export function HistoricalDues({ dues, points_on_contract = 1 }: { dues: Due[]; points_on_contract: number }) {
    const ref = useRef<HTMLCanvasElement>(null)
    const [elementsToShow, setElementsToShow] = useState(elementsToShowMobile)
    const [endAt, setEndAt] = useState(() => dues.length)
    useEffect(() => {
        const handler = () => {
            const newElToShow = window.innerWidth < 500 ? elementsToShowMobile : elementsToShowDesktop
            if (elementsToShow !== newElToShow) {
                setElementsToShow(newElToShow)
                if (endAt < newElToShow) {
                    setEndAt(dues.length <= newElToShow ? dues.length : newElToShow)
                }
            }
        }
        handler()
        window.addEventListener("resize", handler)
        return () => window.removeEventListener("resize", handler)
    }, [dues, elementsToShow, endAt])

    const sortedData = useMemo(() => {
        return [...dues].sort((a, b) => +a.year - +b.year).slice(Math.max(0, endAt - elementsToShow), endAt)
    }, [dues, endAt, elementsToShow])
    const chartRef = useRef<Chart>(null)
    useEffect(() => {
        const data = sortedData.map((due) => parseFloat(due.value.replace("$", "")) * points_on_contract)
        const labels = sortedData.map((due) => due.year)
        if (chartRef.current) {
            chartRef.current.data.datasets[0].data = data
            chartRef.current.data.labels = labels
            chartRef.current.update()
            return
        }
        let delayed = false
        chartRef.current = new Chart(ref.current, {
            type: "bar",
            options: {
                layout: {
                    padding: {
                        top: 30,
                        bottom: 0
                    }
                },
                plugins: {
                    datalabels: {
                        color: "#FFE4A0",
                        display: true,
                        font: {
                            size: 10,
                            family: "Poppins",
                            weight: "normal"
                        },
                        formatter: function (value) {
                            return "$" + Math.round(value)
                        }
                    },
                    tooltip: {
                        backgroundColor: "rgba(0,0,0,.6)",
                        callbacks: {
                            title() {
                                return ""
                            },
                            label: function (tooltipItem) {
                                const label = tooltipItem.label
                                const value = tooltipItem.dataset.data[tooltipItem.dataIndex].toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 1
                                })
                                return " " + label + ": " + value
                            }
                        }
                    }
                },
                animation: {
                    onComplete: () => {
                        delayed = true
                    },
                    delay: (context) => {
                        let delay = 0
                        if (context.type === "data" && context.mode === "default" && !delayed) {
                            delay = context.dataIndex * 20 + context.datasetIndex
                        }
                        return delay
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            format: {
                                currency: "$"
                            },
                            autoSkip: true,
                            font: { size: 12, weight: "bold" },
                            color: "#CFCFCF"
                        },
                        border: { width: 1, color: "#3B3060" },
                        grid: {
                            lineWidth: 1,
                            color: "#FFF",
                            drawOnChartArea: false,
                            drawTicks: true,
                            tickLength: 8,
                            tickColor: "#fffff000"
                        }
                    },
                    y: {
                        ticks: {
                            font: { size: 12, weight: "bold" },
                            callback: function (value) {
                                return value.toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 0
                                })
                            },
                            autoSkip: true,
                            color: "#CFCFCF"
                        },
                        border: { width: 1, display: true, color: "#3B3060" },
                        grid: {
                            tickLength: 8,
                            drawOnChartArea: false,
                            drawTicks: true,
                            tickColor: "#fffff000"
                        },
                        beginAtZero: false
                    }
                },
                color: "#CFCFCF"
            },
            data: {
                labels,
                datasets: [
                    {
                        backgroundColor: "#FFE4A0",
                        borderWidth: 0,
                        barThickness: 12,
                        borderRadius: 100,
                        data,
                        datalabels: {
                            align: "end",
                            anchor: "end"
                        }
                    }
                ]
            }
        })
    }, [sortedData, points_on_contract])
    const onPrev = () => {
        setEndAt((endAt) => Math.max(endAt - elementsToShow, elementsToShow))
    }
    const onNext = () => {
        setEndAt((endAt) => Math.min(endAt + elementsToShow, dues.length))
    }
    return (
        <div className={styles.historicalDues}>
            <h3>Historic annual dues for this contract</h3>
            <canvas ref={ref} />
            {dues.length > elementsToShow && (
                <div className={styles.historicalDuesNav}>
                    <button onClick={onPrev} className={styles.historicalDuesPrev} disabled={endAt === elementsToShow}>
                        <div className={styles.boxInner} />
                        Prev
                        <LongArrowDown />
                    </button>
                    <button onClick={onNext} className={styles.historicalDuesNext} disabled={endAt === dues.length}>
                        <div className={styles.boxInner} />
                        Next
                        <LongArrowDown />
                    </button>
                </div>
            )}
        </div>
    )
}
